import React, { useEffect, useState } from "react";
import { Box, Button, Flex, HStack, Heading, Icon, IconButton, Spinner, Text, VStack, useToast } from "@chakra-ui/react";
import { PageHeader } from "@lubbezposrednio-frontend/commons";
import { FiKey, FiCopy, FiTrash2, FiPlus } from "react-icons/fi";
import { ApiKey, createApiKey, listApiKeys, revokeApiKey } from "@lubbezposrednio-frontend/core";
import { Link as RouterLink } from "react-router-dom";
import { OrganizerPage } from "../OrganizerDashboardView/components/OrganizerShell";

export const ApiKeysView: React.FC = () => {
  const toast = useToast();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [freshToken, setFreshToken] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await listApiKeys();
      setKeys(res.data.api_keys);
    } catch {
      setKeys([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Klucz API";
    load();
  }, []);

  const generate = async () => {
    setCreating(true);
    try {
      const res = await createApiKey();
      setFreshToken(res.data.api_key.token);
      await load();
    } catch {
      toast({ title: "Nie udało się wygenerować klucza", status: "error", isClosable: true });
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id: string) => {
    try {
      await revokeApiKey(id);
      await load();
      toast({ title: "Klucz unieważniony", status: "info", duration: 2000 });
    } catch {
      toast({ title: "Nie udało się unieważnić klucza", status: "error", isClosable: true });
    }
  };

  const copy = (text: string) => {
    navigator.clipboard?.writeText(text);
    toast({ title: "Skopiowano do schowka", status: "success", duration: 1500 });
  };

  const fmt = (d: string | null) => (d ? new Date(d).toLocaleString("pl-PL") : "—");

  return (
    <OrganizerPage header={<PageHeader title="Klucz API" subtitle="Twój klucz dostępu do publicznego API." />}>
      <VStack align="stretch" spacing="16px">
        {freshToken && (
          <Box bg="rgba(31,138,76,0.08)" border="1px solid rgba(31,138,76,0.4)" borderRadius="14px" p="16px">
            <Text fontWeight="800" color="#1f8a4c" mb="6px">
              Nowy klucz — skopiuj teraz (pokazujemy go tylko raz)
            </Text>
            <HStack bg="white" borderRadius="10px" p="10px" border="1px solid #e6e1f0">
              <Text fontFamily="mono" fontSize="14px" wordBreak="break-all" flex="1">
                {freshToken}
              </Text>
              <IconButton aria-label="Kopiuj" icon={<FiCopy />} size="sm" onClick={() => copy(freshToken)} />
            </HStack>
            <Text fontSize="12px" color="gray.500" mt="6px">
              W bazie trzymamy tylko skrót — nie da się go później odzyskać. Jeśli zgubisz klucz, wygeneruj nowy.
            </Text>
          </Box>
        )}

        <Box bg="white" borderRadius="16px" border="1px solid #efeaf8" p="20px">
          <Flex justify="space-between" align="center" mb="14px" gap="12px" wrap="wrap">
            <Box>
              <Heading fontSize="16px" color="gray.800">Twoje klucze</Heading>
              <Text fontSize="13px" color="gray.500">Użyj klucza w nagłówku <Text as="span" fontFamily="mono">Authorization: Bearer …</Text></Text>
            </Box>
            <Button leftIcon={<FiPlus />} bg="primary" color="white" _hover={{ bg: "#360078" }} isLoading={creating} onClick={generate}>
              Wygeneruj klucz
            </Button>
          </Flex>

          {loading ? (
            <Flex justify="center" py="30px"><Spinner color="primary" /></Flex>
          ) : keys.length === 0 ? (
            <Flex direction="column" align="center" py="36px" color="gray.500">
              <Icon as={FiKey} boxSize="26px" color="primary" mb="10px" />
              <Text>Nie masz jeszcze żadnego klucza. Wygeneruj pierwszy powyżej.</Text>
            </Flex>
          ) : (
            <VStack align="stretch" spacing="10px">
              {keys.map(k => (
                <Flex key={k.id} align="center" justify="space-between" gap="12px" p="12px 14px" border="1px solid #efeaf8" borderRadius="12px" opacity={k.revoked ? 0.55 : 1}>
                  <Box minW="0">
                    <HStack>
                      <Text fontFamily="mono" fontWeight="700" color="gray.800">{k.token_prefix}</Text>
                      <Text fontSize="12px" fontWeight="700" color={k.revoked ? "gray.400" : "#1f8a4c"}>
                        {k.revoked ? "unieważniony" : "aktywny"}
                      </Text>
                    </HStack>
                    <Text fontSize="12px" color="gray.500">
                      {k.name} · utworzony {fmt(k.created_at)} · ostatnio użyty {fmt(k.last_used_at)}
                    </Text>
                  </Box>
                  {!k.revoked && (
                    <IconButton aria-label="Unieważnij" icon={<FiTrash2 />} size="sm" variant="ghost" colorScheme="red" onClick={() => revoke(k.id)} />
                  )}
                </Flex>
              ))}
            </VStack>
          )}
        </Box>

        <Text fontSize="13px" color="gray.500">
          Jak używać klucza? Zobacz{" "}
          <Text as={RouterLink} to="/organizator/dokumentacja" color="primary" fontWeight="700" textDecoration="underline">
            dokumentację API
          </Text>.
        </Text>
      </VStack>
    </OrganizerPage>
  );
};
